import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import APIClient from "@/APIClient";
import type { Legend } from "@/types/legend.types";
import type { Ref } from "vue";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import { QueryKey } from "./query-keys";
import { legendDwgCacheRepository } from "@/repositories/legendDwgCache.repository";
import { Logger } from "@/logger";

async function cacheLegendDwg(legend: Legend): Promise<void> {
  if (!legend.dwg_file_info?.download_url) return;
  const { id, dwg_file_info } = legend;
  try {
    const cached = await legendDwgCacheRepository.get(id, dwg_file_info.id);
    if (cached) return;
    const response = await fetch(dwg_file_info.download_url);
    if (!response.ok) return;
    const buffer = await response.arrayBuffer();
    await legendDwgCacheRepository.put(id, dwg_file_info.id, buffer);
  } catch {
    // silently fail — will retry next time
  }
}

async function cacheLegendsDwg(legends: Legend[]): Promise<void> {
  await Promise.allSettled(legends.map(cacheLegendDwg));
}

export const useGetAllLegends = () =>
  useQuery({
    queryKey: [QueryKey.LEGENDS, { group: "all" }],
    queryFn: async () => {
      return await APIClient.fetchAllLegends();
    },
  });

export const useGetMyLegends = () =>
  useQuery({
    queryKey: [QueryKey.LEGENDS, { group: "mine" }],
    queryFn: async () => {
      const res = await APIClient.fetchMyLegends();
      $ls.setItem(LocalStorageKey.LEGENDS, JSON.stringify(res));
      // Keep the DWG cache in sync with the legends the user actually has:
      // drop cached files for legends no longer returned (deleted/unassigned),
      // then cache DWGs for the current ones.
      legendDwgCacheRepository
        .pruneToLegendIds(res.map((legend) => legend.id))
        .catch((e) =>
          Logger.warn(
            `useGetMyLegends() : failed to prune legend DWG cache - ${e?.message}`,
          ),
        );
      cacheLegendsDwg(res); // fire-and-forget: cache DWGs for offline use
      return res;
    },
  });

export const useGetLegend = (id: Ref<number | undefined>) =>
  useQuery({
    queryKey: [QueryKey.LEGENDS, id],
    queryFn: async () => {
      if (!id.value) throw new Error("No legend id provided");
      return await APIClient.fetchLegend(id.value);
    },
    enabled: () => !!id.value,
  });

export const useUpdateLegend = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      legend: {
        id: number;
      } & Partial<Legend>,
    ) => {
      const { id, ...payload } = legend;
      return await APIClient.updateLegend(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.LEGENDS] });
    },
  });
};
export const useUpdateLegendUsers = (id: Ref<number>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (users: number[]) => {
      return await APIClient.updateLegendUsers(id.value, users);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.LEGENDS] });
    },
  });
};

export const useCreateLegend = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (legend: Omit<Legend, "id">) => {
      return await APIClient.createLegend(legend);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.LEGENDS] });
    },
  });
};

export const useDeleteLegend = (id: Ref<number>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await APIClient.deleteLegend(id.value);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.LEGENDS] });
    },
  });
};

export const useDuplicateLegend = (id: Ref<number>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return await APIClient.duplicateLegend(id.value);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.LEGENDS] });
    },
  });
};

export const useLinkLegendDwg = (legendId: Ref<number>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fileId: number) => {
      return await APIClient.linkLegendDwg(legendId.value, fileId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.LEGENDS] });
    },
  });
};

export const useUnlinkLegendDwg = (legendId: Ref<number>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await APIClient.unlinkLegendDwg(legendId.value);
      await legendDwgCacheRepository.remove(legendId.value);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.LEGENDS] });
    },
  });
};
